import React from "react";

interface StatusBadgeProps {
  value: string | number;
  type?: "status_code" | "status" | "priority" | "action_status" | "method";
}

function getStyle(value: string | number, type?: string): React.CSSProperties {
  const base: React.CSSProperties = {
    display: "inline-block",
    fontSize: 11,
    fontWeight: 600,
    padding: "4px 10px",
    borderRadius: 999,
    letterSpacing: "0.03em",
    border: "1px solid transparent",
    lineHeight: 1.3,
    minWidth: 44,
    textAlign: "center",
    color: "var(--badge-text)",
  };

  const makeStyle = (bg: string, _fg?: string, border = "transparent") => ({
    ...base,
    background: bg,
    borderColor: border,
  });

  if (type === "status_code") {
    const code = Number(value);
    if (code < 300) return makeStyle("rgba(16,185,129,0.16)", "#047857", "rgba(16,185,129,0.3)");
    if (code < 400) return makeStyle("rgba(59,130,246,0.16)", "#1d4ed8", "rgba(59,130,246,0.3)");
    if (code < 500) return makeStyle("rgba(245,158,11,0.18)", "#92400e", "rgba(245,158,11,0.25)");
    return makeStyle("rgba(239,68,68,0.18)", "#b91c1c", "rgba(239,68,68,0.25)");
  }

  if (type === "status") {
    return String(value).toLowerCase() === "success"
      ? makeStyle("rgba(16,185,129,0.16)", "#047857", "rgba(16,185,129,0.3)")
      : makeStyle("rgba(239,68,68,0.18)", "#b91c1c", "rgba(239,68,68,0.25)");
  }

  if (type === "priority") {
    if (value === "High")   return makeStyle("rgba(239,68,68,0.18)",   "#b91c1c", "rgba(239,68,68,0.25)");
    if (value === "Medium") return makeStyle("rgba(245,158,11,0.18)",  "#92400e", "rgba(245,158,11,0.25)");
    return makeStyle("rgba(59,130,246,0.16)", "#1d4ed8", "rgba(59,130,246,0.3)");
  }

  if (type === "action_status") {
    if (value === "Resolved") return makeStyle("rgba(16,185,129,0.16)", "#047857", "rgba(16,185,129,0.3)");
    if (value === "Ignored")  return makeStyle("rgba(100,116,139,0.12)", "var(--muted)", "rgba(100,116,139,0.22)");
    return makeStyle("rgba(245,158,11,0.18)", "#92400e", "rgba(245,158,11,0.25)");
  }

  if (type === "method") {
    const colors: Record<string, { bg: string; border: string }> = {
      GET:    { bg: "rgba(59,130,246,0.16)",  border: "rgba(59,130,246,0.3)"  },
      POST:   { bg: "rgba(16,185,129,0.16)",  border: "rgba(16,185,129,0.3)"  },
      PUT:    { bg: "rgba(245,158,11,0.18)",  border: "rgba(245,158,11,0.25)" },
      DELETE: { bg: "rgba(239,68,68,0.18)",   border: "rgba(239,68,68,0.25)"  },
      PATCH:  { bg: "rgba(168,85,247,0.16)",  border: "rgba(168,85,247,0.28)" },
    };
    const c = colors[String(value).toUpperCase()] ?? { bg: "rgba(148,163,184,0.16)", border: "rgba(148,163,184,0.25)" };
    return makeStyle(c.bg, undefined, c.border);
  }

  return makeStyle("var(--surface-soft)", "var(--muted)", "var(--border)");
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ value, type }) => (
  <span style={getStyle(value, type)}>{value}</span>
);

export default StatusBadge;
